import type { Notification } from '@scrambl/shared'
import { NotificationType } from '@scrambl/shared'

type NotificationData = Record<string, unknown>

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const buildQuery = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value)
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

function resolveTimesheetNotificationRoute(
  orgSlug: string,
  notification: Notification,
): string | undefined {
  const data = (notification.data || {}) as NotificationData
  const recipientRole = asString(data.recipientRole)
  const action = asString(data.action)
  const entryId = asString(data.entryId)
  const payPeriodId = asString(data.payPeriodId)
  const targetUserId =
    asString(data.targetUserId) || asString(data.userId) || notification.actorId

  if (recipientRole === 'APPROVER' && targetUserId) {
    return `/${orgSlug}/timesheets/approvals/${targetUserId}${buildQuery({
      periodId: payPeriodId,
      entryId,
    })}`
  }

  if (
    recipientRole === 'OWNER' &&
    (action === 'rejected' || action === 'changes_requested') &&
    entryId
  ) {
    if (payPeriodId) {
      return `/${orgSlug}/my-time/timesheets${buildQuery({
        payPeriodId,
        entryId,
      })}`
    }
    return `/${orgSlug}/my-time/timesheets${buildQuery({ entryId })}`
  }

  if (recipientRole === 'OWNER' && payPeriodId) {
    return `/${orgSlug}/my-time/timesheets${buildQuery({ payPeriodId })}`
  }

  if (entryId) {
    return `/${orgSlug}/my-time/timesheets${buildQuery({ entryId })}`
  }

  return undefined
}

/**
 * Types that have no in-app destination by construction. Delivery for these is
 * a different channel entirely, so a click never happens — but the coverage
 * test needs the intent written down rather than inferred from a missing case.
 */
const TYPES_WITHOUT_IN_APP_ROUTE: ReadonlySet<string> = new Set<string>([
  // Email-only digest: no notification row is ever written for it.
  NotificationType.RESOURCE_WEEKLY_DIGEST,
])

export function getNotificationRoute(
  orgSlug: string | undefined,
  notification: Notification,
): string | undefined {
  if (!orgSlug) return undefined
  if (TYPES_WITHOUT_IN_APP_ROUTE.has(notification.type)) return undefined

  if (
    notification.type === NotificationType.TIMESHEET_ENTRY_SUBMITTED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_APPROVED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_REJECTED ||
    notification.type === NotificationType.TIMESHEET_ENTRY_CHANGES_REQUESTED ||
    notification.type === NotificationType.TIMESHEET_SUBMITTED ||
    notification.type === NotificationType.TIMESHEET_APPROVED ||
    notification.type === NotificationType.TIMESHEET_REJECTED ||
    notification.type === NotificationType.TIMESHEET_CHANGES_REQUESTED ||
    notification.type === NotificationType.TIMESHEET_CLOSE_REMINDER ||
    notification.type === NotificationType.TIMESHEET_MISSING_REMINDER ||
    notification.type === NotificationType.TIMESHEET_APPROVAL_DEADLINE
  ) {
    return resolveTimesheetNotificationRoute(orgSlug, notification)
  }

  // The tagged entry surfaces as a suggestion on the Log tab, not as a row on
  // the recipient's timesheet — they have not copied it yet.
  if (notification.type === NotificationType.TIME_ENTRY_TAGGED) {
    return `/${orgSlug}/my-time/log`
  }

  // Overdue billing review (invoicing-ops Phase 4): land on the Monthly
  // billing workspace with the review's month selected and the client's row
  // expanded. entityId carries the client; data.period the YYYY-MM month.
  if (notification.type === NotificationType.CLIENT_BILLING_REVIEW_OVERDUE) {
    const period = (notification.data as { period?: string } | null)?.period
    const params = new URLSearchParams()
    if (period) params.set('period', period)
    if (notification.entityId) params.set('entity', notification.entityId)
    const query = params.toString()
    return `/${orgSlug}/financials/monthly${query ? `?${query}` : ''}`
  }

  if (
    notification.type === NotificationType.IMPORT_COMPLETED ||
    notification.type === NotificationType.IMPORT_FAILED
  ) {
    const moduleRoutes: Record<string, string> = {
      TASK: 'tasks',
      CONTACT: 'contacts',
      LEAD: 'leads',
      DEAL: 'deals',
      // The clients list lives at /entities — /clients is not a route, so this
      // used to land imports of client records on a 404.
      ENTITY: 'entities',
      PROJECT: 'projects',
    }
    const moduleType = asString(notification.data?.moduleType)
    return `/${orgSlug}/${moduleRoutes[moduleType || ''] || 'tasks'}`
  }

  // Agent review requests land in the review queue, not the task itself
  if (notification.type === 'AGENT_REVIEW_REQUESTED') {
    return `/${orgSlug}/tasks/reviews`
  }

  // Live chat responder notifications ride TICKET_CREATED (the DB enum is
  // closed) and are identified by data.liveChatEvent; they open the chat
  // inbox with the session preselected — never a ticket.
  if (asString(notification.data?.liveChatEvent)) {
    const sessionId = asString(notification.data?.sessionId)
    return `/${orgSlug}/live-chat?tab=inbox${
      sessionId ? `&session=${sessionId}` : ''
    }`
  }

  // Meeting recordings: ready notes open the generated note; failures (or
  // notes-less recordings) land on the recordings list.
  if (
    notification.type === NotificationType.MEETING_NOTES_READY ||
    notification.type === NotificationType.MEETING_NOTES_FAILED
  ) {
    const noteId = asString(notification.data?.noteId)
    if (notification.type === NotificationType.MEETING_NOTES_READY && noteId) {
      return `/${orgSlug}/notes/${noteId}`
    }
    return `/${orgSlug}/notes/recordings`
  }

  // Budget, authorization and forecast alerts all carry entityType FINANCIALS
  // with a SYNTHETIC entityId (a dedupe key like "budget:client:90"), so they
  // must be resolved from `data` before the entityType switch ever sees them.
  if (
    notification.type === NotificationType.BUDGET_CHANGE_REQUESTED ||
    notification.type === NotificationType.BUDGET_CHANGE_APPROVED ||
    notification.type === NotificationType.BUDGET_CHANGE_REJECTED ||
    notification.type === NotificationType.BUDGET_USAGE_70 ||
    notification.type === NotificationType.BUDGET_USAGE_90 ||
    notification.type === NotificationType.BUDGET_OVERAGE ||
    notification.type === NotificationType.BUDGET_PROJECTED_OVERAGE
  ) {
    const projectId = asString(notification.data?.projectId)
    if (projectId) return `/${orgSlug}/projects/${projectId}`
    const clientId = asString(notification.data?.entityId)
    if (clientId) return `/${orgSlug}/entities/${clientId}`
    return notification.type === NotificationType.BUDGET_CHANGE_REQUESTED
      ? `/${orgSlug}/financials/approvals`
      : `/${orgSlug}/financials/overview`
  }

  if (
    notification.type === NotificationType.AUTHORIZATION_EXPIRING ||
    notification.type === NotificationType.AUTHORIZATION_LOW_REMAINING
  ) {
    const clientId = asString(notification.data?.entityId)
    if (clientId) return `/${orgSlug}/entities/${clientId}`
    return `/${orgSlug}/financials/billing`
  }

  // Platform announcements address no record. They carry their own link when
  // ops set one; otherwise the notifications page is the only honest landing
  // spot — better than a click that visibly does nothing.
  if (
    notification.type === NotificationType.SYSTEM_MAINTENANCE ||
    notification.type === NotificationType.FEATURE_ANNOUNCEMENT ||
    notification.type === NotificationType.SUBSCRIPTION_EXPIRING ||
    notification.type === NotificationType.SUBSCRIPTION_RENEWED
  ) {
    const dataUrl = asString(notification.data?.url)
    if (dataUrl && dataUrl.startsWith('/') && !dataUrl.startsWith('//')) {
      return dataUrl
    }
    return `/${orgSlug}/notifications`
  }

  // Family loop (personal spaces). Stars have no record of their own — the
  // hearth home is where streaks and the weekly recap are shown.
  if (
    notification.type === NotificationType.STREAK_MILESTONE ||
    notification.type === NotificationType.STARS_WEEKLY_SUMMARY
  ) {
    return `/${orgSlug}/today`
  }

  // Collection date reminders address a collection ENTRY, which is only
  // addressable through its collection's slug.
  if (notification.type === NotificationType.COLLECTION_DATE_REMINDER) {
    const slug = asString(notification.data?.collectionSlug)
    const entryId =
      asString(notification.data?.entryId) || asString(notification.entityId)
    if (slug && entryId) return `/${orgSlug}/collections/${slug}/${entryId}`
    if (slug) return `/${orgSlug}/collections/${slug}`
    return `/${orgSlug}/collections`
  }

  // Project-watcher rows can hang off a task, a document or the project. Only
  // the project is guaranteed, so it is the fallback when the source record is
  // not addressable.
  const isProjectWatchType = notification.type.startsWith('PROJECT_WATCH_')

  if (!notification.entityType) {
    // Workflow-authored and system notifications may carry their own
    // destination. Only ever trust in-app paths — never an absolute URL.
    const dataUrl = asString(notification.data?.url)
    if (dataUrl && dataUrl.startsWith('/') && !dataUrl.startsWith('//')) {
      return dataUrl
    }
    if (isProjectWatchType) {
      const projectId = asString(notification.data?.projectId)
      if (projectId) return `/${orgSlug}/projects/${projectId}`
    }
    return undefined
  }
  if (!notification.entityId && notification.entityType !== 'TICKET') {
    if (isProjectWatchType) {
      const projectId = asString(notification.data?.projectId)
      if (projectId) return `/${orgSlug}/projects/${projectId}`
    }
    return undefined
  }

  // Comment notifications carry the comment id — append it so the detail
  // view can scroll to and highlight the exact comment (useScrollToComment).
  const commentQuery = buildQuery({
    comment: asString(notification.data?.commentId),
  })

  switch (notification.entityType) {
    case 'TASK': {
      const taskIdentifier =
        asString(notification.data?.taskNumber) || notification.entityId
      return `/${orgSlug}/tasks/${taskIdentifier}${commentQuery}`
    }
    case 'TICKET': {
      const ticketIdentifier =
        asString(notification.data?.ticketNumber) ||
        asString(notification.entityId) ||
        asString(notification.data?.ticketId)
      if (!ticketIdentifier) return undefined
      return `/${orgSlug}/tickets/${ticketIdentifier}${commentQuery}`
    }
    case 'PROJECT':
      return `/${orgSlug}/projects/${notification.entityId}${commentQuery}`
    case 'THREAD': {
      const commentId = asString(notification.data?.commentId)
      return `/${orgSlug}/threads?tab=discussions&thread=${notification.entityId}${
        commentId ? `&comment=${commentId}` : ''
      }`
    }
    case 'NOTE': {
      const noteId =
        asString(notification.data?.noteId) || notification.entityId
      return `/${orgSlug}/notes/${noteId}`
    }
    case 'ENTITY':
      return `/${orgSlug}/entities/${notification.entityId}`
    case 'INVOICE':
      return `/${orgSlug}/invoices/${notification.entityId}`
    case 'DEAL':
      return `/${orgSlug}/deals/${notification.entityId}`
    // Contract approval notifications land on the contract overview, where the
    // approval panel lives.
    case 'CONTRACT':
      return `/${orgSlug}/contracts/${notification.entityId}`
    case 'DEMAND':
      return `/${orgSlug}/team/demand/${notification.entityId}`
    case 'CONTACT':
      return `/${orgSlug}/contacts/${notification.entityId}`
    case 'LEAD':
      return `/${orgSlug}/leads/${notification.entityId}`
    case 'EVENT':
      return `/${orgSlug}/events/${notification.entityId}`
    case 'QUOTE':
      return `/${orgSlug}/quotes/${notification.entityId}`
    case 'DOCUMENT':
      return `/${orgSlug}/documents/${notification.entityId}`
    // Burnout alerts address a member, whose profile lives under Team.
    case 'MEMBER':
      return `/${orgSlug}/team/people/${notification.entityId}`
    // Sprints have no standalone page — open the project that owns them.
    case 'SPRINT': {
      const projectId = asString(notification.data?.projectId)
      return projectId ? `/${orgSlug}/projects/${projectId}` : undefined
    }
    // Synthetic per-week id; the recap is rendered on the hearth home.
    case 'STARS_WEEK':
      return `/${orgSlug}/today`
    case 'CUSTOM_COLLECTION_ENTRY': {
      const slug = asString(notification.data?.collectionSlug)
      return slug
        ? `/${orgSlug}/collections/${slug}/${notification.entityId}`
        : `/${orgSlug}/collections`
    }
    // Automation-authored notifications from the workflow engine.
    case 'WORKFLOW':
      return `/${orgSlug}/workflows`
    // The only TIME_ENTRY-tagged notification is the time report export
    // receipt, whose job id is not addressable — land on the reports tab.
    case 'TIME_ENTRY':
      return `/${orgSlug}/timesheets/reports`
    default: {
      if (isProjectWatchType) {
        const projectId = asString(notification.data?.projectId)
        if (projectId) return `/${orgSlug}/projects/${projectId}`
      }
      return undefined
    }
  }
}
